import { useMemo } from 'react';
import type { Href } from 'expo-router';
import { useIntl } from 'react-intl';

import {
  useScreenHeaderAddressQuery,
  useScreenHeaderRestaurantQuery,
} from '../../../../../ScreenHeader/graphql/ScreenHeader.generated';
import { LINKS } from '../../../../NavbarV2.constant';
import { navbarV2Messages as messages } from '../../../../NavbarV2.messages';

import type { LastInteractedStore } from '../../../../../../context/LastInteractedStore';

// ─────────────────────────────────────────────────────────────────────────────

export const useNavbarLocationData = (params: Params) => {
  const { lastInteractedStore } = params;

  const { formatMessage } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  const addressId = lastInteractedStore?.addressId ?? '';
  const restaurantId = lastInteractedStore?.restaurantId ?? '';
  const restaurantSlug = lastInteractedStore?.restaurantSlug;

  // ─── Remote Data ─────────────────────────────────────────────────────

  const [{ data: addressData }] = useScreenHeaderAddressQuery({
    variables: { addressId },
    pause: !addressId,
    requestPolicy: 'cache-first',
  });

  const [{ data: restaurantData }] = useScreenHeaderRestaurantQuery({
    variables: { id: restaurantId },
    pause: !restaurantId,
    requestPolicy: 'cache-first',
  });

  // ─── Derived ─────────────────────────────────────────────────────────

  const addressName = addressData?.address?.name;
  const restaurant = restaurantData?.restaurant;

  const orderChannel = useMemo(() => {
    if (addressId) return 'delivery';
    if (restaurant?.isOutpost) return 'outpost';

    return 'pickup';
  }, [addressId, restaurant?.isOutpost]);

  const locationName = addressId ? addressName : restaurant?.name;
  const locationTitle = locationName ?? formatMessage(messages.findLocationNearYou);

  const locationEyebrow = lastInteractedStore
    ? formatMessage(messages.locationEyebrow, { orderChannel })
    : undefined;

  const menuHref = useMemo<Href>(() => {
    if (addressId) {
      return { pathname: '/delivery/[addressId]/menu', params: { addressId } };
    }

    if (restaurantSlug) {
      return { pathname: '/[restaurantSlug]/menu', params: { restaurantSlug } };
    }

    return LINKS.LOCATION_SELECTION;
  }, [addressId, restaurantSlug]);

  // ─────────────────────────────────────────────────────────────────────

  return { locationEyebrow, locationTitle, menuHref };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  lastInteractedStore: LastInteractedStore;
};
